import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Lock, AlertTriangle, Clock } from "lucide-react";

const SharedDemoPage = ({ onDemoAccess }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("validating"); // validating, valid, expired, error
  const [linkData, setLinkData] = useState(null);

  useEffect(() => {
    const validateLink = async () => {
      try {
        const res = await axios.get(`${API}/demo-links/validate/${token}`);
        setLinkData(res.data);
        setStatus("valid");
        
        // Store the demo access token, watermark, and session for tracking
        localStorage.setItem("token", res.data.access_token);
        localStorage.setItem("demo_watermark", res.data.watermark);
        localStorage.setItem("demo_session_id", res.data.session_id);
        localStorage.setItem("demo_link_token", token);
        
        // Auto-redirect to demo after brief display
        setTimeout(() => {
          onDemoAccess(res.data.access_token, res.data.watermark);
          navigate("/auto-demo", { replace: true });
        }, 2500);
      } catch (err) {
        if (err.response?.status === 410) {
          setStatus("expired");
        } else {
          setStatus("error");
        }
      }
    };

    if (token) {
      validateLink();
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-[#090a0d] flex items-center justify-center p-4" data-testid="shared-demo-page">
      <div className="max-w-md w-full text-center space-y-6">
        {status === "validating" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#F97316]/20 flex items-center justify-center mx-auto animate-pulse">
              <Lock className="w-8 h-8 text-[#F97316]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">Validating Access</h1>
            <p className="text-white/50">Verifying your demo link...</p>
            <div className="flex justify-center">
              <div className="w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin" />
            </div>
          </>
        )}

        {status === "valid" && linkData && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto">
              <Lock className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">Access Granted</h1>
            <p className="text-white/50">{linkData.label}</p>
            <div className="p-3 rounded-lg bg-white/5 border border-white/10 inline-block">
              <span className="text-white/40 text-xs">
                {linkData.views_remaining} views remaining
              </span>
            </div>
            <p className="text-[#F97316] text-sm">Launching executive demo...</p>
            <div className="flex justify-center">
              <div className="w-8 h-8 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin" />
            </div>
          </>
        )}

        {status === "expired" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
              <Clock className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">Link Expired</h1>
            <p className="text-white/50">
              This demo link has expired or reached its maximum views.
              Please contact the sender for a new link.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">Invalid Link</h1>
            <p className="text-white/50">
              This demo link is not valid. It may have been revoked or does not exist.
            </p>
          </>
        )}

        {/* Security footer */}
        <div className="pt-6 border-t border-white/10">
          <p className="text-white/20 text-xs">
            This content is confidential and protected. All access is logged and watermarked.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedDemoPage;
